import type { GrantRow } from "../../shared/rbac/grantQueries";
import { type GrantEntry, type RoleOption } from "../../shared/rbac/roles";

type Principal = {
  userGroupId?: string;
  userId?: string;
  serviceAccountId?: string;
};

// The custom roles defined for a given scope kind (e.g. "space", "user_group").
export function customRolesForScope<T extends { scopeKind: string }>(
  customRoles: readonly T[],
  scopeKind: string
): T[] {
  return customRoles.filter((r) => r.scopeKind === scopeKind);
}

export type ManageAccessWarning = "viewer-loses" | "orphaned" | null;

// Decides whether committing `next` over `current` should prompt a confirmation:
// the viewer would drop their own manage access ("viewer-loses"), or the scope
// would be left with no manager at all ("orphaned"). Returns null when neither
// applies. Both manager and viewer-retention checks are injected so each scope
// can define "manager" (e.g. space:manage vs user_group:write) and how the
// viewer holds it (direct grant vs group membership).
export function manageAccessWarning({
  current,
  next,
  isManagerGrant,
  viewerRetainsManage,
}: {
  current: GrantEntry[];
  next: GrantEntry[];
  isManagerGrant: (grant: GrantEntry) => boolean;
  viewerRetainsManage: (grants: GrantEntry[]) => boolean;
}): ManageAccessWarning {
  if (viewerRetainsManage(current) && !viewerRetainsManage(next))
    return "viewer-loses";
  if (current.some(isManagerGrant) && !next.some(isManagerGrant))
    return "orphaned";
  return null;
}

function matchesRole(g: GrantEntry, opt: RoleOption): boolean {
  return (
    (!!opt.builtinRoleId && g.builtinRoleId === opt.builtinRoleId) ||
    (!!opt.customRoleId && g.customRoleId === opt.customRoleId)
  );
}

function matchesPrincipal(g: GrantEntry, principal: Principal): boolean {
  if (principal.userGroupId) return g.userGroupId === principal.userGroupId;
  if (principal.userId) return g.userId === principal.userId;
  if (principal.serviceAccountId)
    return g.serviceAccountId === principal.serviceAccountId;
  return false;
}

export function grantedGroupIds(grants: GrantEntry[]): Set<string> {
  const ids = new Set<string>();
  for (const g of grants) if (g.userGroupId) ids.add(g.userGroupId);
  return ids;
}

export function grantedUserIds(grants: GrantEntry[]): Set<string> {
  const ids = new Set<string>();
  for (const g of grants) if (g.userId) ids.add(g.userId);
  return ids;
}

export function grantedPrincipalKeys(grants: GrantEntry[]): Set<string> {
  const keys = new Set<string>();
  for (const g of grants) {
    if (g.userGroupId) keys.add(`group:${g.userGroupId}`);
    if (g.userId) keys.add(`user:${g.userId}`);
    if (g.serviceAccountId) keys.add(`serviceAccount:${g.serviceAccountId}`);
  }
  return keys;
}

// Resolves staged grant entries (which carry only a customRoleId) into the
// GrantRow shape the RBAC availability checks read, joining each custom role's
// permissions so cross-resource rank checks work client-side.
export function grantEntriesToRows(
  grants: readonly GrantEntry[],
  customRoles: readonly {
    id: string;
    rolePermissions: ReadonlyArray<{ permission: string }>;
  }[]
): GrantRow[] {
  const roleMap = new Map(customRoles.map((r) => [r.id, r]));
  return grants.map((g) => ({
    userGroupId: g.userGroupId ?? null,
    userId: g.userId ?? null,
    serviceAccountId: g.serviceAccountId ?? null,
    builtinRoleId: g.builtinRoleId ?? null,
    customRole: g.customRoleId ? (roleMap.get(g.customRoleId) ?? null) : null,
  }));
}

export function grantForMutation(g: GrantEntry): GrantEntry {
  return {
    userGroupId: g.userGroupId,
    userId: g.userId,
    serviceAccountId: g.serviceAccountId,
    builtinRoleId: g.builtinRoleId,
    customRoleId: g.customRoleId,
  };
}

export function sameRoleOption(a: RoleOption, b: RoleOption): boolean {
  return (
    (!!a.builtinRoleId && a.builtinRoleId === b.builtinRoleId) ||
    (!!a.customRoleId && a.customRoleId === b.customRoleId)
  );
}

export function spaceFullyStaged(
  space: {
    groups: readonly { id: string }[];
    userIds: readonly string[];
    serviceAccountIds?: readonly string[];
    rolesByGroupId?: Record<string, RoleOption[]>;
    rolesByUserId?: Record<string, RoleOption[]>;
    rolesByServiceAccountId?: Record<string, RoleOption[]>;
  },
  stagedRolesByKey: ReadonlyMap<string, readonly RoleOption[]>
): boolean {
  const covered = (key: string, needed: readonly RoleOption[] | undefined) => {
    const have = stagedRolesByKey.get(key);
    if (!have) return false;
    return (needed ?? []).every((r) => have.some((h) => sameRoleOption(h, r)));
  };
  return (
    space.groups.every((g) =>
      covered(`group:${g.id}`, space.rolesByGroupId?.[g.id])
    ) &&
    space.userIds.every((uid) =>
      covered(`user:${uid}`, space.rolesByUserId?.[uid])
    ) &&
    (space.serviceAccountIds ?? []).every((said) =>
      covered(`serviceAccount:${said}`, space.rolesByServiceAccountId?.[said])
    )
  );
}

export function dedupeStaged<T extends { key: string }>(
  additions: T[],
  existingKeys: ReadonlySet<string>,
  committedKeys: ReadonlySet<string>
): T[] {
  const have = new Set(existingKeys);
  const out: T[] = [];
  for (const a of additions) {
    if (have.has(a.key) || committedKeys.has(a.key)) continue;
    have.add(a.key);
    out.push(a);
  }
  return out;
}

type StagedRow = { key: string; roles: RoleOption[] };

export function applyMatchToStaged<T extends StagedRow>(
  prev: T[],
  additions: T[],
  isSpace: boolean,
  committedKeys: ReadonlySet<string>
): T[] {
  if (!isSpace) {
    const existingKeys = new Set(prev.map((s) => s.key));
    const newItems = dedupeStaged(additions, existingKeys, committedKeys);
    return newItems.length > 0 ? [...prev, ...newItems] : prev;
  }
  const byKey = new Map(prev.map((s) => [s.key, s] as const));
  for (const a of additions) {
    const existing = byKey.get(a.key);
    if (!existing) {
      byKey.set(a.key, a);
      continue;
    }
    const roles = [...existing.roles];
    for (const r of a.roles)
      if (!roles.some((x) => sameRoleOption(x, r))) roles.push(r);
    byKey.set(a.key, { ...existing, roles });
  }
  return [...byKey.values()];
}

// The inclusive range of items between two keys, in list order, regardless of
// which key comes first; empty when either key is absent.
export function matchesInRange<T>(
  items: readonly T[],
  keyOf: (item: T) => string,
  anchorKey: string | null,
  targetKey: string | null
): T[] {
  if (!anchorKey || !targetKey) return [];
  const a = items.findIndex((i) => keyOf(i) === anchorKey);
  const b = items.findIndex((i) => keyOf(i) === targetKey);
  if (a < 0 || b < 0) return [];
  const [lo, hi] = a < b ? [a, b] : [b, a];
  return items.slice(lo, hi + 1);
}

export function toggleGrantRole(
  allGrants: GrantEntry[],
  principal: Principal,
  principalGrants: GrantEntry[],
  opt: RoleOption
): GrantEntry[] {
  const hasRole = principalGrants.some((g) => matchesRole(g, opt));
  if (hasRole && principalGrants.length <= 1) return allGrants;

  if (hasRole) {
    return allGrants.filter(
      (g) => !(matchesPrincipal(g, principal) && matchesRole(g, opt))
    );
  }
  return [
    ...allGrants,
    {
      ...principal,
      builtinRoleId: opt.builtinRoleId,
      customRoleId: opt.customRoleId,
    },
  ];
}
